import {CheckCircleOutlined, WarningOutlined} from '@ant-design/icons';
import {Alert, Button, Card, Form, Input, Modal, Spin } from 'antd';
import React, { useState } from 'react';
import {useDataProvider, useNotify, usePermissions, useQueryWithStore} from 'react-admin';
import Editor from '@monaco-editor/react';
import { DiffOutlined } from '@ant-design/icons';
import DeclarationDiff from "../ChangeInstances/DeclarationDiff";
const { TextArea } = Input;
import ChangeLogPanel from "@src/components/ChangeInstances/List/ChangeLogPanel";
import ChangeDetailBar from "@src/components/ChangeInstances/List/ChangeDetailBar";

const { confirm } = Modal;
const spinnerElement = <Spin size="large" description="Loading"><Card style={{height: "100px"}}></Card></Spin>

const ChangeInstanceRow = (
    { record, refetch, refresh, extraProps }:
    { record: Record<string, any>, refetch: Function,  refresh: Function, extraProps?: any }
) => {
    const { permissions } = usePermissions();
    const [isUpdateFormVisible, setIsUpdateFormVisible] = useState(false);
    const [completeChangeInstanceForm] = Form.useForm();
    const [logForm] = Form.useForm();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const logModalMessageGenerator = (newState: string) => {
        if(newState==="ERROR")
            return "Please provide a reason for error below (optional):"

        else if(newState==="REJECTED")
            return "Please provide a reason for rejection below (optional):"

        else if(newState==="APPROVED")
            return "Additional Information (optional):"

        else if(newState==="COMPLETED")
            return "Additional Information (optional):"

    }

    const onSubmitCompleteChangeInstance = (values: any) => {
        setIsUpdateFormVisible(false)
        const log_ = completeChangeInstanceForm.getFieldValue("log")
        // The comment is optional, and the box it comes from starts empty — so it
        // only travels when there is something in it. Sent blank it overwrites
        // whatever is already on the change instance, the AI validator's verdict
        // included.
        const submissionData: any = { state: "COMPLETED" }
        if (log_ && log_.trim())
            submissionData.log = log_

        const deployed_item = values?.deployed_item
        if(deployed_item)
            if(!(deployed_item==="" || (typeof deployed_item === "object" && Object.keys(deployed_item).length === 0)))
                submissionData['deployed_item'] = JSON.parse(deployed_item)

        dataProvider.update('owner_change_instances', { id: record.id, data: submissionData, previousData: { id: record.id } })
            .then(response => {
                refresh();
                refetch();
                notify("Change Instance's state has been updated", "success", {autoHideDuration: 1500})

            }).catch(error => {
                notify("Something went wrong", "warning")
            })
    }

    const updateChangeInstance = (record: Record<string, any>, newState: string) => {
        const updateCI = () => {
            const log_ = logForm.getFieldValue("log")
            // same as above: a blank comment says nothing, so it sends nothing
            const updateCIData: any = {state: newState};
            if (log_ && log_.trim())
                updateCIData.log = log_

            dataProvider.update(
                'owner_change_instances', {
                    id: record.id,
                    data: updateCIData,
                    previousData: { id: record.id }
                })
                .then(response => {
                    refresh();
                    refetch();
                    notify("Change Instance's state has been updated", "success", {autoHideDuration: 1500})
                })
                .catch(error => notify("Something went wrong", "warning"))
        };

        if (["REJECTED", "ERROR", "APPROVED"].includes(newState)) {
            const alertMessage = (newState=="REJECTED"?'Rejecting':'Erroring') + " this change instance will require the customer to modify their declaration before they can submit to NetOrca again."
            confirm({
                icon: <></>,
                content: (
                    <>
                        {
                            ["REJECTED", "ERROR"].includes(newState) &&
                            <>
                                <Alert message={alertMessage} type="warning"/>
                                <br/>
                            </>
                        }
                        {logModalMessageGenerator(newState)}
                        <Form form={logForm}>
                            <Form.Item name={"log"} initialValue={""}>
                                <TextArea rows={4} name="log"/>
                            </Form.Item>
                        </Form>
                    </>
                ),
                onOk() { updateCI() },
            });
        } else {
            updateCI()
        }
    }

    const CompleteChangeInstanceModal = () => (
        <Modal title="Complete Change Instance"
            open={isUpdateFormVisible}
            footer={[
                <Button key="back" onClick={() => setIsUpdateFormVisible(false)}>Cancel</Button>,
                <Button key="submit" type="primary" onClick={completeChangeInstanceForm.submit}>Complete</Button>
            ]}
            closable={false}
        >
            <Form
                form={completeChangeInstanceForm}
                name="update_change"
                className="row-col"
                onFinish={onSubmitCompleteChangeInstance}
                layout="vertical"
            >
                <Form.Item label="Deployed Item JSON: (optional)" id='deployed_item' name="deployed_item">
                    <Editor height="30vh" defaultLanguage="json" defaultValue="{}" />
                </Form.Item>
                {logModalMessageGenerator("COMPLETED")}
                <Form.Item name={"log"} initialValue={""}>
                    <TextArea rows={4} name="log"/>
                </Form.Item>
            </Form>
        </Modal>
    )

    const permissionChangeState = ["ADMIN", "GENERAL"].includes(permissions);

    const { data: serviceData } = useQueryWithStore ({
        type: 'getOne',
        resource: "services",
        payload: { id: record?.service?.id }
    });

    const isDependantPending =
        record.state == 'PENDING' && permissionChangeState && record.is_dependant == true;

    /* No wrapping Card: this renders inside the entry's own panel, and a
       nested card would inset the diff away from the full width. */
    return (
        serviceData ?
        <div className="ci-detail">
            <ChangeDetailBar record={record} resource={extraProps?.resource}>
                {/* destructive transitions are marked danger rather than
                    looking identical to the confirming ones */}
                {record.state == 'PENDING' && permissionChangeState && record.is_dependant == false && serviceData.allow_manual_approval &&
                    <Button onClick={() => updateChangeInstance(record, "APPROVED")} icon={<CheckCircleOutlined />} type="primary">Approve</Button>
                }
                {record.state == 'PENDING' && permissionChangeState && record.is_dependant == false &&
                    <Button onClick={() => updateChangeInstance(record, "REJECTED")} icon={<WarningOutlined />} danger>Reject</Button>
                }
                {record.state == 'APPROVED' && permissionChangeState && serviceData.allow_manual_completion &&
                    <Button onClick={() => setIsUpdateFormVisible(true)} type="primary" icon={<CheckCircleOutlined />}>Complete</Button>
                }
                {record.state == 'APPROVED' && permissionChangeState &&
                    <Button onClick={() => updateChangeInstance(record, "ERROR")} icon={<WarningOutlined />} danger>Mark as Error</Button>
                }
                {["ERROR", "REJECTED"].includes(record.state) && permissionChangeState &&
                    <Button onClick={() => updateChangeInstance(record, "CLOSED")} icon={<CheckCircleOutlined />} type="primary">Close</Button>
                }
            </ChangeDetailBar>

            {isDependantPending &&
                <Alert
                    className="ci-detail__notice"
                    type="warning"
                    showIcon
                    message="Dependant pending change instances must be approved or rejected by the main owner"
                />
            }

            <ChangeLogPanel log={record.log} />

            <section className="ci-block">
                <header className="ci-block__head">
                    <DiffOutlined className="ci-block__icon" aria-hidden="true" />
                    <h4 className="ci-block__title">Declaration diff</h4>
                </header>
                <div className="ci-diff">
                    <DeclarationDiff change_instance={record} />
                </div>
            </section>

            <CompleteChangeInstanceModal />
        </div>
            :
            spinnerElement
    )
}

export default ChangeInstanceRow;